import { useState } from "react";
import { motion } from "framer-motion";

export default function PrivacyPolicy() {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: "introduction",
      title: "Introduction & Commitment",
      icon: "🔒",
      content: `Techspear Ventures LLC together with its affiliated entities is committed to protecting your privacy. We have prepared this Privacy Policy to describe our practices regarding the Personal Data we collect from users of www.techspearventures.com and all related websites and online services.`
    },
    {
      id: "contact",
      title: "Contact Information",
      icon: "📞",
      content: `If you have any questions or concerns about our Privacy Policy or our data collection practices, please contact us:

• Email: Info@techspearventures.com
• Phone: 404-206-2653
• Address: Techspear Ventures, 75 5th Street NW, Suite 427, Atlanta, Georgia 30308`
    },
    {
      id: "consent",
      title: "User Consent",
      icon: "✓",
      content: `By submitting Personal Data through our Site or Services, you agree to the terms of this Privacy Policy and you expressly consent to the collection, use, and disclosure of your Personal Data in accordance with this Privacy Policy.`
    },
    {
      id: "international",
      title: "International Users",
      icon: "🌍",
      content: `The Site is hosted in the United States. If you are a non-U.S. user, by visiting the Site or utilizing the Services, you acknowledge that your Personal Data may be processed in the United States and other countries where data protection laws may differ from your country's laws.`
    },
    {
      id: "children",
      title: "Children's Privacy",
      icon: "👶",
      content: `We do not intentionally gather Personal Data from visitors under the age of 18. If we learn that we have collected information from a child under 18, we will attempt to delete it as soon as possible.`
    },
    {
      id: "data-types",
      title: "Types of Data We Collect",
      icon: "📊",
      content: `• Personal Data: Information that identifies you (name, email, address, phone, etc.)
• Anonymous Data: Data that cannot identify individual persons
• We collect both types of data as described in our policy`
    },
    {
      id: "provided-info",
      title: "Information You Provide",
      icon: "👤",
      content: `• Account information (name, email, password, professional details)
• Mobile device information
• Feedback and contact information
• Public profile content
• Survey responses
• Sweepstakes and contest entries`
    },
    {
      id: "technical-info",
      title: "Technical Information Collected",
      icon: "🔧",
      content: `• Browser type, operating system, IP address
• Log files and clickstream data
• Cookies and similar technologies
• Pixel Tags for email tracking
• Google Analytics data
• This helps us improve our services and user experience`
    },
    {
      id: "data-use",
      title: "How We Use Your Data",
      icon: "🎯",
      content: `• Create and secure your account
• Provide and improve our services
• Send administrative communications
• Respond to your inquiries
• Send updates and marketing materials (with your consent)
• Analyze usage patterns`
    },
    {
      id: "data-sharing",
      title: "Data Sharing & Disclosure",
      icon: "🤝",
      content: `• With other users for service purposes
• Third-party service providers
• Affiliated companies
• During corporate restructuring
• When required by law or legal process
• To protect rights and safety`
    },
    {
      id: "third-party",
      title: "Third-Party Services",
      icon: "🔗",
      content: `Our Site may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review their privacy policies.`
    },
    {
      id: "your-choices",
      title: "Your Choices & Rights",
      icon: "⚙️",
      content: `• Email Communications: Unsubscribe from marketing emails
• Cookies: Manage through browser settings
• Social Media: Unlink accounts in preferences
• Access & Correction: Update your profile information
• Deletion: Request data deletion (subject to legal requirements)`
    },
    {
      id: "data-retention",
      title: "Data Retention",
      icon: "💾",
      content: `We retain Personal Data only as long as necessary for business needs or legal requirements. When no longer needed, we securely delete or anonymize your information.`
    },
    {
      id: "changes",
      title: "Policy Changes",
      icon: "🔄",
      content: `We may update this Privacy Policy periodically. We will notify you of material changes by email or by posting a notice on our Site. Continued use after changes indicates acceptance.`
    },
    {
      id: "security",
      title: "Security & Training",
      icon: "🛡️",
      content: `We conduct regular employee training on data protection and review our security measures annually to ensure the safety of your Personal Data.`
    }
  ];

  const dataCategories = [
    {
      title: "Personal Data",
      items: ["Name", "Email Address", "Phone Number", "Professional Title", "Company Name", "Mailing Address"],
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Technical Data",
      items: ["IP Address", "Browser Type", "Device Information", "Cookies", "Usage Analytics", "Log Files"],
      color: "from-green-500 to-green-600"
    },
    {
      title: "Usage Data",
      items: ["Pages Visited", "Time Spent", "Features Used", "Click Patterns", "Survey Responses", "Communication Preferences"],
      color: "from-purple-500 to-purple-600"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-16 font-sans tracking-wide">
      <div className="px-4 sm:px-6 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-6">
            🛡️ Privacy & Data Protection
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-mokoto text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-gray-600">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>Last Updated: July 10, 2020</span>
            </div>
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>Your Privacy Matters</span>
            </div>
          </div>
        </motion.div>

        {/* Data Categories Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
        >
          {dataCategories.map((category, index) => (
            <div key={category.title} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
              <div className={`w-12 h-12 bg-gradient-to-r ${category.color} rounded-xl flex items-center justify-center text-white text-lg font-bold mb-4`}>
                {index + 1}
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">{category.title}</h3>
              <ul className="space-y-2">
                {category.items.map((item, idx) => (
                  <li key={idx} className="flex items-center text-gray-600">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-blue-600 to-green-600 rounded-2xl p-8 text-white mb-12"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold mb-2">18+</div>
              <div className="text-blue-100 text-sm">Minimum Age</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">24/7</div>
              <div className="text-blue-100 text-sm">Support Available</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">100%</div>
              <div className="text-blue-100 text-sm">Opt-out Freedom</div>
            </div>
            <div>
              <div className="text-3xl font-bold mb-2">SSL</div>
              <div className="text-blue-100 text-sm">Data Encryption</div>
            </div>
          </div>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-12"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  toggleSection(section.id);
                  document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-left p-3 rounded-lg bg-gray-50 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 border border-transparent hover:border-blue-200"
              >
                <div className="text-lg mb-1">{section.icon}</div>
                <span className="text-sm font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Content Sections */}
        <div className="space-y-6">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
              className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full px-6 py-6 text-left flex justify-between items-center hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl">{section.icon}</div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{section.title}</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      Click to {openSections[section.id] ? 'collapse' : 'expand'} details
                    </p>
                  </div>
                </div>
                <svg
                  className={`w-6 h-6 text-gray-500 transform transition-transform duration-200 ${
                    openSections[section.id] ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <motion.div
                initial={false}
                animate={{ 
                  height: openSections[section.id] ? 'auto' : 0,
                  opacity: openSections[section.id] ? 1 : 0
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="px-6 pb-6 border-t border-gray-200 pt-6">
                  <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                    {section.content.split('\n').map((paragraph, idx) => (
                      <p key={idx} className="mb-4 last:mb-0">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Your Rights Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-gradient-to-r from-green-500 to-blue-600 rounded-2xl p-8 text-white mt-16"
        >
          <h2 className="text-3xl font-bold mb-6 text-center">Your Privacy Rights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "👁️", title: "Right to Access", desc: "View your personal data" },
              { icon: "✏️", title: "Right to Correct", desc: "Update inaccurate information" },
              { icon: "🗑️", title: "Right to Delete", desc: "Request data removal" },
              { icon: "🚫", title: "Right to Opt-out", desc: "Stop marketing communications" }
            ].map((right, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl mb-3">{right.icon}</div>
                <h3 className="font-semibold mb-2">{right.title}</h3>
                <p className="text-green-100 text-sm">{right.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center mt-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Questions About Your Privacy?</h2>
          <p className="text-gray-600 text-lg mb-6 max-w-2xl mx-auto">
            We're committed to protecting your data and being transparent about our practices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href="mailto:Info@techspearventures.com"
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <span>📧</span>
              <span>Email Our Team</span>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}